// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';

export interface ChatRoom {
  id: string;
  type: 'dm' | 'group';
  name?: string | null;
  owner_id?: string | null;
  last_message_at?: string | null;
  last_message_preview?: string | null;
  unread_count?: number;
}

export interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string | null;
  attachment_url: string | null;
  attachment_mime: string | null;
  created_at: string;
  soft_deleted_by_admin_at?: string | null;
}

export const listMyRooms = async (): Promise<ChatRoom[]> => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) return [] as any[];

  const { data: parts, error: pErr } = await supabase
    .from('chat_room_participants')
    .select('room_id')
    .eq('user_id', me);
  if (pErr) throw pErr;
  const roomIds = Array.from(new Set((parts || []).map((p: any) => p.room_id)));
  if (roomIds.length === 0) return [];
  const { data: rooms, error: rErr } = await supabase
    .from('chat_rooms')
    .select('id,type,name,last_message_at,last_message_preview,owner_id,participants:chat_room_participants(user_id)')
    .in('id', roomIds)
    .order('last_message_at', { ascending: false });
  if (rErr) throw rErr;
  // Deduplicate: for DM rooms, collapse multiple rooms with same counterpart(s)
  const byKey: Record<string, any> = {};
  for (const r of rooms || []) {
    const participants = ((r as any).participants || []).map((p: any) => p.user_id);
    const others = participants.filter((id: string) => id !== me).sort();
    const dmKey = (r as any).type === 'dm' ? `dm:${others.join(',')}` : `group:${(r as any).id}`;
    const existing = byKey[dmKey];
    if (!existing) {
      byKey[dmKey] = { ...r, unread_count: (r as any).unread_count || 0 };
    } else {
      const a = new Date((existing as any).last_message_at || 0).getTime();
      const b = new Date((r as any).last_message_at || 0).getTime();
      if (b > a) byKey[dmKey] = { ...r, unread_count: (r as any).unread_count || 0 };
    }
  }
  return Object.values(byKey) as any[];
};

export const listRoomParticipants = async (roomId: string): Promise<string[]> => {
  const { data, error } = await supabase
    .from('chat_room_participants')
    .select('user_id')
    .eq('room_id', roomId);
  if (error) throw error;
  return (data || []).map((r: any) => r.user_id);
};

export const listMessages = async (roomId: string, limit = 100): Promise<ChatMessage[]> => {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('room_id', roomId)
    .order('created_at', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return data || [];
};

export const listReceiptsForMessages = async (messageIds: string[]) => {
  if (messageIds.length === 0) return [] as any[];
  const { data, error } = await supabase
    .from('chat_message_receipts')
    .select('*')
    .in('message_id', messageIds);
  if (error) throw error;
  return data || [];
};

export const getProfilesByIds = async (ids: string[]) => {
  if (!ids || ids.length === 0) return [] as any[];
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email, avatar_url, last_seen')
    .in('id', ids);
  if (error) throw error;
  return data || [];
};

export const getProfilesByEmails = async (emails: string[]) => {
  if (!emails || emails.length === 0) return [] as any[];
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email, avatar_url, last_seen')
    .in('email', emails);
  if (error) throw error;
  return data || [];
};

export const subscribeToRoomMessages = (roomId: string, onChange: (payload?: any) => void) => {
  const channel = supabase
    .channel(`chat-room-${roomId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_messages', filter: `room_id=eq.${roomId}` }, (payload) => {
      onChange(payload);
    })
    .subscribe();
  return () => {
    channel.unsubscribe();
  };
};

export const ensureDmRoom = async (otherUserId: string): Promise<string> => {
  // Use RPC to avoid RLS recursive policy issues
  const { data, error } = await supabase.rpc('ensure_dm_room', { other_user: otherUserId });
  if (error) throw error;
  return data as string;
};

export const createGroupRoom = async (name: string, memberIds: string[]): Promise<string> => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) throw new Error('Not authenticated');

  const { data: created, error: createError } = await supabase
    .from('chat_rooms')
    .insert({ type: 'group', name, owner_id: me })
    .select('id')
    .single();
  if (createError) throw createError;
  const roomId = created.id;
  const ids = Array.from(new Set([me, ...memberIds]));
  const rows = ids.map((id) => ({ room_id: roomId, user_id: id, role: id === me ? 'owner' : 'member' }));
  const { error: partErr } = await supabase.from('chat_room_participants').insert(rows);
  if (partErr) throw partErr;
  return roomId;
};

export const sendMessage = async (roomId: string, content: string | null, attachment: { url: string; mime: string } | null) => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) throw new Error('Not authenticated');

  const payload: any = { room_id: roomId, sender_id: me };
  if (content) payload.content = content;
  if (attachment) {
    payload.attachment_url = attachment.url;
    payload.attachment_mime = attachment.mime;
  }

  const { error } = await supabase.from('chat_messages').insert(payload);
  if (error) throw error;
};

export const markRead = async (messageIds: string[]) => {
  const { data: session } = await supabase.auth.getUser();
  const me = session.user?.id;
  if (!me) return;
  if (messageIds.length === 0) return;
  // Basic dedupe
  const unique = Array.from(new Set(messageIds));
  const rows = unique.map((id) => ({ message_id: id, user_id: me }));

  const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));
  let attempt = 0;
  const maxAttempts = 3;
  while (attempt < maxAttempts) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await supabase.from('chat_message_receipts').upsert(rows, { onConflict: 'message_id,user_id' });
      return;
    } catch (e: any) {
      const msg = e?.message || '';
      // Swallow 403 until RLS is live
      if (e?.code === '403' || msg.includes('403')) return;
      attempt += 1;
      if (attempt >= maxAttempts) throw e;
      // eslint-disable-next-line no-await-in-loop
      await sleep(150 * attempt);
    }
  }
};

export const adminDeleteMessage = async (messageId: string) => {
  const { error } = await supabase.rpc('admin_hard_delete_message', { p_message_id: messageId });
  if (error) throw error;
};

export const adminDeleteAllMessagesInRoom = async (roomId: string) => {
  const { data, error } = await supabase.rpc('admin_delete_all_messages_in_room', { p_room_id: roomId });
  if (error) throw error;
  return data as number;
};


